import type { FeatureCollection } from 'geojson';

import { pointInPolygon } from '@/utils/mapUtils';
import { AREA_FOCUS_MIN_ZOOM, mapLibreZoomToSemanticZoomLevel, SemanticZoomLevel } from './semanticZoom';

/**
 * The geographic scope the Global Stats bar is currently showing: the whole
 * network, the Area of Interest under the map centre (the Area of Interest
 * level), or — from the DMA level inward — the DMA under the map centre,
 * falling back to the containing Area of Interest.
 */
export type StatsScope =
    | { type: 'network' }
    | { type: 'area'; name: string }
    | { type: 'dma'; ident: string; name: string };

/** Stable identity for the network scope so repeated resolutions compare equal. */
export const NETWORK_SCOPE: StatsScope = { type: 'network' };

/** Stable identity key for change-detection and cache keys. */
export function statsScopeKey(scope: StatsScope): string {
    switch (scope.type) {
        case 'network':
            return 'network';
        case 'area':
            return `area:${scope.name}`;
        case 'dma':
            return `dma:${scope.ident}`;
    }
}

/**
 * Resolves which stats scope the map centre falls in at the given MapLibre zoom.
 * Pure containment maths against the unclipped polygon collections held by the
 * layer hooks — deliberately independent of layer visibility toggles.
 *
 * Three zones:
 * - Network Overview (below AREA_FOCUS_MIN_ZOOM): the whole client network is in
 *   view, so the Global Stats bar keeps its overall figures.
 * - Area of Interest level (AREA_FOCUS_MIN_ZOOM up to the DMA band): the AOI
 *   containing the centre, else network. DMAs are ignored here — not shown yet.
 * - DMA level and inward: first DMA containing the centre, else the first
 *   containing AOI (graceful fallback), else network.
 */
export function resolveStatsScope(
    centre: [number, number],
    zoom: number,
    areas: FeatureCollection,
    dmas: FeatureCollection,
): StatsScope {
    // NaN comparisons are all false, which would drop NaN into the area-focus
    // branch below — bail to the network scope for any non-finite zoom instead.
    if (!Number.isFinite(zoom)) return NETWORK_SCOPE;

    const level = mapLibreZoomToSemanticZoomLevel(zoom);
    if (level > SemanticZoomLevel.DistrictMeterArea) {
        if (zoom < AREA_FOCUS_MIN_ZOOM) return NETWORK_SCOPE;
        return firstContainingArea(centre, areas) ?? NETWORK_SCOPE;
    }

    for (const feature of dmas.features) {
        const ident = feature.properties?.ident as string | undefined;
        if (!ident) continue;
        if (pointInPolygon(centre, feature.geometry)) {
            const name = feature.properties?.name as string | undefined;
            return { type: 'dma', ident, name: name ?? ident };
        }
    }

    return firstContainingArea(centre, areas) ?? NETWORK_SCOPE;
}

function firstContainingArea(centre: [number, number], areas: FeatureCollection): StatsScope | null {
    for (const feature of areas.features) {
        const name = feature.properties?.name as string | undefined;
        if (!name) continue;
        if (pointInPolygon(centre, feature.geometry)) {
            return { type: 'area', name };
        }
    }
    return null;
}
